import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import IconCheck from '@BUP/assets/Icons/check_icon.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import styles from './sociogram-teacher-table.module.scss'
import { useNavigate } from 'react-router-dom'
import { monoCourse, multiCourse } from '@BUP/constants'
import ButtonComponent from '@BUP/components/Generics/Button'
import Table from '..'
import { CourseContext } from '@BUP/contexts/CourseContext'
import { useSociogramActions } from '@BUP/hooks/Sociogram/useSociogramContext'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { findNivel } from '@BUP/services/BraveUp'
import ActionsCell from '../SociogramTable/actions-cell'
import { CurrentSociogramContext } from '@BUP/contexts/CurrentSociogramContext'
import { useGetUsersListByCourse } from '@BUP/hooks/Enrollments/useGetUsersListByCourse'
import { useGetSurveyParticipantsTerminateds } from '@BUP/hooks/Sociogram/participantsTerminated/useGetSurveyParticipantsTerminateds'
import CollapseInfoTeacherSociogram from '../../CollapsePanel/CollapseInfoTeacherSociogram'

const SociogramTeacherTable = () => {
  const [t] = useTranslation('translation', {
    keyPrefix: 'pages.data.sociogram.teacher'
  })
  const navigate = useNavigate()
  const { dataCurrentSociogram } = useContext(CurrentSociogramContext)
  const { currentSociogram: sociogram } = dataCurrentSociogram || {}
  const { surveys } = sociogram || []

  const organization = useContext(OrganizationContext)
  const { type: organizationType } = organization

  const isMonoCourse = organizationType === monoCourse
  const isMultiCourse = organizationType === multiCourse

  const { locale } = organization.location
  const course = useContext(CourseContext)

  const courseId = course && course[0].courseId

  const { usersTotalCount, isLoadingUsers } = useGetUsersListByCourse(
    courseId,
    'STUDENT'
  )

  const filterSurvey =
    surveys &&
    surveys.length > 0 &&
    surveys.filter(survey => survey.courses[0].id === courseId)

  const currentSurvey = filterSurvey && filterSurvey[0]

  const { isLoadingParticipantsTerminated, participantsTerminated } =
      useGetSurveyParticipantsTerminateds(currentSurvey.id)

  const surveyWithCourseInfo =
  filterSurvey &&
  filterSurvey.map(elem => {
    return {
      ...elem,
      courseId: currentSurvey.courses[0].id,
      courseName: currentSurvey.courses[0].name,
      count: usersTotalCount,
      participantsTerminated: participantsTerminated || []
    }
  })

  const { setSelectedSurvey } = useSociogramActions()

  const isDoneSurvey = surveyWithCourseInfo && surveyWithCourseInfo[0] ? surveyWithCourseInfo[0].isDone : false
  const isActive = surveyWithCourseInfo && surveyWithCourseInfo[0] ? surveyWithCourseInfo[0].isActive : false

  const handleViewCourseList = elem => {
    const surveyId = elem?.original?.id || null
    navigate(`/sociogram/students-list/${surveyId}`)
    setSelectedSurvey(surveyId ? elem?.original : null)
  }

  const uniqueParticipantsTerminated = participantsTerminatedRow => {
    const accum = {}
    for (const participant of participantsTerminatedRow) {
      accum[participant.enrollmentId] = participant
    }
    const noDuplicatedParticipantsTerminatedRow = Object.values(accum)
    return noDuplicatedParticipantsTerminatedRow
  }

  const columns = [
    {
      Header: `${t('table.headerName')}`,
      accessor: 'name',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { courseName, courses } = row.original
        const course = courses[0]
        const nivel = findNivel(locale, course)
        const SociogramName = isMonoCourse ? courseName : nivel.name
        const isDone = row?.original?.isDone
        return (
          <div className={styles.name_cell}>
            {isDone
              ? (
                <div>
                  <img
                    src={IconCheck}
                    alt='Icon check'
                  />
                </div>
                )
              : null}
            <span>{SociogramName}</span>{' '}
          </div>
        )
      }
    },
    {
      Header: `${t('table.headerState')}`,
      accessor: 'isActive',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const isDone = row?.original?.isDone
        return (
          <div className={styles.state_cell}>
            <span>
              {!value && !isDone
                ? `${t('table.notActiveState')}`
                : value && !isDone
                  ? `${t('table.activeState')}`
                  : `${t('table.closedState')}`}
            </span>
          </div>
        )
      }
    },
    {
      Header: `${t('table.headerResponses')}`,
      accessor: 'responses',
      disableSortBy: true,
      Cell: ({ row }) => {
        const count = parseInt(row?.original?.count, 10)
        const participantsTerminatedRow = row?.original?.participantsTerminated
        const noDuplicatedParticipantsTerminatedRow =
          uniqueParticipantsTerminated(participantsTerminatedRow)
        const result = row?.original?.result
        const numberOfParticipants = row?.original?.participants?.length || 0
        return (
          <div className={styles.responses_cell_wrapper}>
            <span
              style={
                numberOfParticipants &&
                numberOfParticipants > 0 &&
                noDuplicatedParticipantsTerminatedRow?.length >=
                  numberOfParticipants &&
                !result
                  ? { color: '#17BEBB' }
                  : numberOfParticipants > 0 &&
                    noDuplicatedParticipantsTerminatedRow?.length >=
                      numberOfParticipants &&
                    result
                    ? { color: '#6441A5' }
                    : { color: '#898A89' }
              }
              className={styles.responses_text}
            >
              <span>{t('table.responsesText')}</span>
              {noDuplicatedParticipantsTerminatedRow?.length}/
              {numberOfParticipants || count}
            </span>
            <ButtonComponent
              buttonText={`${t('table.listButton')}`}
              colorType='primary'
              icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              size='small'
              bold={false}
              onClick={() => {
                handleViewCourseList(row)
              }}
              hideTextInMobile
              disabled={!isActive && !isDoneSurvey}
            />
          </div>
        )
      }
    },
    {
      Header: `${t('table.headerActions')}`,
      accessor: 'isDone',
      disableSortBy: true,
      Cell: ({ value: isDone, row }) => {
        const { original: survey } = row
        const isActive = survey.isActive
        const surveyIsDone = !isActive && isDone

        const count = parseInt(survey?.count, 10)
        if (!isActive && !isDone && count > 0 && count < 6) {
          return (
            <span style={{ fontWeight: 'bold' }}>
              {t('table.noMinimumTextActiveSociogram')}
            </span>
          )
        }
        if (!surveyIsDone && count >= 6) {
          return (
            <div className={styles.button_cell_wrapper}>
              <ActionsCell
                survey={survey}
                isMultiCourse={isMultiCourse}
                sociogram={sociogram}
                isTeacher
              />
            </div>
          )
        }
        if (surveyIsDone && count >= 6) {
          return (
            <div className={styles.icon_cell}>
              <img
                src={IconCheck}
                alt='Icon check'
              />
            </div>
          )
        }
        return (
          <span style={{ fontWeight: 'bold' }}>
            {t('table.emptyCourseText')}
          </span>
        )
      }
    }
  ]

  return (
    <>
      {currentSurvey && !currentSurvey.isActive && !currentSurvey.isDone
        ? (
          <CollapseInfoTeacherSociogram />
          )
        : null}
      <Table
        data={surveyWithCourseInfo || []}
        columnsArray={columns}
        shadow
        smallLastChild={isDoneSurvey}
        fitContentTable
        loading={isLoadingUsers || isLoadingParticipantsTerminated}
      />
    </>
  )
}

export default SociogramTeacherTable
